import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, Plus, Play, Trash, X, ArrowLeft, Lightning, Crown, Image as ImageIcon, Microphone, UploadSimple } from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import ReferenceMediaUpload from '../components/ReferenceMediaUpload';
import ShareSaveButton from '../components/ShareSaveButton';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_LABELS = {
  queued: 'Queued…',
  writing_script: 'Writing the script…',
  generating_slides: 'Designing the 4 slides…',
  generating_voiceover: 'Recording the voiceover…',
  stitching_video: 'Stitching the video…',
  queueing_sora: 'Queueing Sora 2…',
  generating_sora_video: 'Sora 2 is animating your scene…',
  finalizing: 'Finalizing…',
  ready: 'Ready',
  failed: 'Failed',
};

export default function AnimationSeriesPage() {
  const { user } = useAuth();
  const [series, setSeries] = useState([]);
  const [activeSeries, setActiveSeries] = useState(null);
  const [activeEpisodes, setActiveEpisodes] = useState([]);
  const [showSeriesForm, setShowSeriesForm] = useState(false);
  const [showEpisodeForm, setShowEpisodeForm] = useState(false);
  const [seriesForm, setSeriesForm] = useState({ name: '', topic: '', description: '', target_episode_count: 5, business_url: '', extra_context: '' });
  // Zero Friction: hide description/business_url/extra_context behind Customise
  const [seriesShowAdvanced, setSeriesShowAdvanced] = useState(false);
  const [episodeForm, setEpisodeForm] = useState({
    title: '', script_outline: '', extra_prompt: '', duration_seconds: 30, tier: 'slideshow',
    reference_image_url: '', voiceover_url: '', voiceover_text: '',
  });
  const [loading, setLoading] = useState(false);
  const [previewEp, setPreviewEp] = useState(null);
  const [showIdeas, setShowIdeas] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(null); // 'image' | 'voice' | null
  const [assets, setAssets] = useState({ uploads: [], avatars: [], voiceovers: [] });
  const polls = useRef({});

  useEffect(() => {
    loadSeries();
    return () => { Object.values(polls.current).forEach(clearInterval); };
  }, []);

  const loadSeries = async () => {
    try {
      const { data } = await axios.get(`${API}/api/animation/series/list`);
      setSeries(data.series || []);
    } catch { /* ignore */ }
  };

  const loadEpisodes = async (s) => {
    try {
      const { data } = await axios.get(`${API}/api/animation/series/${s.series_id}`);
      setActiveSeries(data.series);
      setActiveEpisodes(data.episodes || []);
      // resume polling for any episodes still generating
      (data.episodes || []).forEach(ep => {
        if (ep.status === 'generating') pollEpisode(ep.episode_id);
      });
    } catch { toast.error('Could not load series'); }
  };

  const loadAssets = async () => {
    try {
      const [a, v] = await Promise.all([
        axios.get(`${API}/api/assets/all`),
        axios.get(`${API}/api/voiceover/list`).catch(() => ({ data: { voiceovers: [] } })),
      ]);
      setAssets({
        uploads: (a.data?.uploads || []).filter(x => x.file_type === 'image'),
        avatars: a.data?.avatars || [],
        voiceovers: v.data?.voiceovers || [],
      });
    } catch { /* ignore */ }
  };

  const createSeries = async () => {
    if (!seriesForm.topic.trim()) { toast.error('Tell us the series topic'); return; }
    setLoading(true);
    try {
      // Auto-derive the series name from the topic if user didn't fill it.
      const payload = { ...seriesForm };
      if (!payload.name.trim()) {
        const t = payload.topic.trim();
        payload.name = t.length > 60 ? t.slice(0, 57) + '…' : t;
      }
      const { data } = await axios.post(`${API}/api/animation/series/create`, payload);
      setSeries(prev => [data, ...prev]);
      setShowSeriesForm(false);
      setSeriesForm({ name: '', topic: '', description: '', target_episode_count: 5, business_url: '', extra_context: '' });
      toast.success('Series created!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not create'); }
    finally { setLoading(false); }
  };

  const fetchIdeas = async () => {
    setIdeasLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/animation/series/ideas`, {
        business_url: seriesForm.business_url, extra_context: seriesForm.extra_context,
      });
      setIdeas(data.ideas || []);
      setShowIdeas(true);
    } catch { toast.error('Could not fetch ideas'); }
    finally { setIdeasLoading(false); }
  };

  const applyIdea = (idea) => {
    setSeriesForm(prev => ({
      ...prev,
      name: idea.name || prev.name, topic: idea.topic || prev.topic, description: idea.description || prev.description,
    }));
    setShowIdeas(false);
    setShowSeriesForm(true);
  };

  const createEpisode = async () => {
    if (!episodeForm.title.trim() || !activeSeries) { toast.error('Title required'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/animation/episode/create`, {
        series_id: activeSeries.series_id, ...episodeForm,
      });
      setActiveEpisodes(prev => [...prev, data]);
      setShowEpisodeForm(false);
      setEpisodeForm({
        title: '', script_outline: '', extra_prompt: '', duration_seconds: 30, tier: 'slideshow',
        reference_image_url: '', voiceover_url: '', voiceover_text: '',
      });
      toast.success(`Episode generating — usually 1–2 min.`);
      pollEpisode(data.episode_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not create episode'); }
    finally { setLoading(false); }
  };

  const pollEpisode = (epId) => {
    if (polls.current[epId]) clearInterval(polls.current[epId]);
    polls.current[epId] = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/animation/episode/${epId}`);
        setActiveEpisodes(prev => prev.map(e => e.episode_id === epId ? data : e));
        if (data.status === 'ready' || data.status === 'failed') {
          clearInterval(polls.current[epId]);
          delete polls.current[epId];
          if (data.status === 'ready') toast.success(`"${data.title}" is ready!`);
          if (data.status === 'failed') toast.error(`"${data.title}" failed: ${data.error || 'unknown error'}`);
        }
      } catch { /* keep polling */ }
    }, 5000);
  };

  const deleteEpisode = async (id) => {
    try {
      await axios.delete(`${API}/api/animation/episode/${id}`);
      setActiveEpisodes(prev => prev.filter(e => e.episode_id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const deleteSeries = async (id) => {
    if (!window.confirm('Delete this series and all its episodes?')) return;
    try {
      await axios.delete(`${API}/api/animation/series/${id}`);
      setSeries(prev => prev.filter(s => s.series_id !== id));
      if (activeSeries?.series_id === id) { setActiveSeries(null); setActiveEpisodes([]); }
      toast.success('Series deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const uploadAndUseAsRef = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/uploads/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (kind === 'image') setEpisodeForm(p => ({ ...p, reference_image_url: data.url }));
      else if (kind === 'voice') setEpisodeForm(p => ({ ...p, voiceover_url: data.url }));
      toast.success('Uploaded — applied as reference');
      setShowAssetPicker(null);
    } catch { toast.error('Upload failed'); }
  };

  const userTier = (user?.tier || user?.plan || 'free').toLowerCase();
  const canUseSora = userTier === 'pro' || userTier === 'premium' || userTier === '29';

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <BrandSettingsSummary fields={['brand','tone','industry']} />
        {!activeSeries ? (
          <>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-4xl sm:text-5xl font-['Instrument_Serif',serif]" style={{ color: '#f5f0e8' }}>Animation Series</h1>
                <p className="mt-2 text-base" style={{ color: 'rgba(245,240,232,0.5)' }}>
                  Build a YouTube Shorts series. Slideshow on Free / $19, full Sora 2 animation on $29.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={fetchIdeas} disabled={ideasLoading} data-testid="suggest-ideas-btn"
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                  style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.2)' }}>
                  {ideasLoading ? <div className="w-3 h-3 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(200,240,96,0.2)', borderTopColor: '#c8f060' }} /> : '✨'}
                  Suggest series ideas
                </button>
                <button onClick={() => setShowSeriesForm(true)} data-testid="new-series-btn"
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: '#c8f060', color: '#080808' }}>
                  <Plus size={16} weight="bold" /> New series
                </button>
              </div>
            </div>

            {series.length === 0 ? (
              <div className="rounded-2xl p-12 text-center" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                <FilmStrip size={48} weight="duotone" style={{ color: 'rgba(200,240,96,0.4)' }} className="mx-auto mb-4" />
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>No series yet. Create one to start producing animated YouTube shorts.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {series.map(s => (
                  <button key={s.series_id} onClick={() => loadEpisodes(s)} data-testid={`series-${s.series_id}`}
                    className="text-left rounded-2xl p-5 transition-all hover:ring-1 hover:ring-white/10"
                    style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.06)' }}>
                        <FilmStrip size={20} style={{ color: '#c8f060' }} />
                      </div>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.5)' }}>
                        {s.episodes_count || 0} eps
                      </span>
                    </div>
                    <p className="text-base font-semibold mb-1" style={{ color: '#f5f0e8' }}>{s.name}</p>
                    <p className="text-xs line-clamp-2" style={{ color: 'rgba(245,240,232,0.4)' }}>{s.topic}</p>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button onClick={() => { setActiveSeries(null); setActiveEpisodes([]); }} className="flex items-center gap-2 text-sm" data-testid="back-to-series"
              style={{ color: 'rgba(245,240,232,0.5)' }}>
              <ArrowLeft size={16} /> All series
            </button>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-4xl sm:text-5xl font-['Instrument_Serif',serif]" style={{ color: '#f5f0e8' }}>{activeSeries.name}</h1>
                <p className="mt-2 text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>{activeSeries.topic}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { loadAssets(); setShowEpisodeForm(true); }} data-testid="new-episode-btn"
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: '#c8f060', color: '#080808' }}>
                  <Plus size={16} weight="bold" /> New episode
                </button>
                <button onClick={() => deleteSeries(activeSeries.series_id)}
                  className="px-3 py-2.5 rounded-xl text-sm" style={{ background: 'rgba(255,68,68,0.06)', color: '#ff4444', border: '1px solid rgba(255,68,68,0.15)' }}>
                  <Trash size={14} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeEpisodes.length === 0 ? (
                <div className="col-span-full rounded-2xl p-12 text-center" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <p className="text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>No episodes yet. Create your first one.</p>
                </div>
              ) : activeEpisodes.map(ep => (
                <div key={ep.episode_id} className="rounded-2xl overflow-hidden group" data-testid={`episode-${ep.episode_id}`}
                  style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="cursor-pointer relative" style={{ aspectRatio: '9/16' }} onClick={() => ep.status === 'ready' && setPreviewEp(ep)}>
                    {ep.status === 'ready' && ep.thumbnail_url ? (
                      <>
                        <img src={`${API}${ep.thumbnail_url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                            <Play size={20} weight="fill" style={{ color: '#fff' }} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center" style={{ background: 'rgba(200,240,96,0.04)' }}>
                        {ep.status === 'failed' ? (
                          <>
                            <p className="text-xs font-semibold" style={{ color: '#ff6b6b' }}>Generation failed</p>
                            <p className="text-[10px] mt-1" style={{ color: 'rgba(245,240,232,0.4)' }}>{ep.error || 'Credits refunded.'}</p>
                          </>
                        ) : (
                          <>
                            <div className="w-7 h-7 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(200,240,96,0.2)', borderTopColor: '#c8f060' }} />
                            <p className="text-[11px] mt-2 font-medium" style={{ color: '#c8f060' }}>{STEP_LABELS[ep.step] || 'Generating…'}</p>
                            <div className="w-3/4 h-1 mt-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                              <div className="h-full transition-all duration-500" style={{ width: `${ep.progress || 0}%`, background: '#c8f060' }} />
                            </div>
                            <p className="text-[10px] mt-1" style={{ color: 'rgba(245,240,232,0.4)' }}>{ep.progress || 0}%</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="p-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#f5f0e8' }}>{ep.title}</p>
                      <p className="text-[10px] flex items-center gap-1" style={{ color: 'rgba(245,240,232,0.4)' }}>
                        {ep.tier === 'sora' ? <><Crown size={9} weight="fill" /> Sora 2</> : <><Lightning size={9} /> Slideshow</>} · {ep.duration_seconds}s
                      </p>
                    </div>
                    <button onClick={() => deleteEpisode(ep.episode_id)} className="p-1.5 rounded hover:bg-white/5">
                      <Trash size={11} style={{ color: '#ff6b6b' }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* New series modal */}
        <AnimatePresence>
          {showSeriesForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowSeriesForm(false)}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.8)' }}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl p-6 space-y-4 my-auto" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-lg font-semibold" style={{ color: '#f5f0e8' }}>Animation Series</p>
                <p className="text-xs" style={{ color: 'rgba(245,240,232,0.4)' }}>A branded short-form series — consistent, ready to publish.</p>

                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-2" style={{ color: 'rgba(245,240,232,0.4)', fontFamily: 'ui-monospace, monospace' }}>Series topic</label>
                  <input value={seriesForm.topic} onChange={e => setSeriesForm({ ...seriesForm, topic: e.target.value })}
                    placeholder="e.g., weekly marketing tips, founder journey, product behind-the-scenes"
                    data-testid="series-topic-input"
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm" style={{ color: '#f5f0e8' }} />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-2" style={{ color: 'rgba(245,240,232,0.4)', fontFamily: 'ui-monospace, monospace' }}>Episodes</label>
                  <div className="flex gap-2">
                    {[3, 5, 10].map(n => (
                      <button key={n} onClick={() => setSeriesForm({ ...seriesForm, target_episode_count: n })} data-testid={`episodes-${n}`}
                        className="px-4 py-2 rounded-full text-xs font-medium transition-all"
                        style={{
                          background: seriesForm.target_episode_count === n ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${seriesForm.target_episode_count === n ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                          color: seriesForm.target_episode_count === n ? '#c8f060' : 'rgba(245,240,232,0.55)',
                        }}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button onClick={createSeries} disabled={loading} data-testid="create-series-submit"
                    className="flex-1 py-3 rounded-full text-sm font-semibold disabled:opacity-50" style={{ background: '#c8f060', color: '#080808' }}>
                    Build my series
                  </button>
                  <button onClick={() => setShowSeriesForm(false)} className="px-4 rounded-full text-sm" style={{ background: 'rgba(255,255,255,0.04)', color: '#f5f0e8' }}>Cancel</button>
                </div>

                <button onClick={() => setSeriesShowAdvanced(v => !v)} data-testid="series-customise-toggle"
                  className="w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-semibold flex items-center justify-between"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {seriesShowAdvanced ? 'Hide customise' : 'Customise →'}
                  </span>
                  <span style={{ color: '#c8f060' }}>{seriesShowAdvanced ? '−' : '+'}</span>
                </button>

                {seriesShowAdvanced && (
                  <div className="space-y-3 pt-1">
                    <input value={seriesForm.name} onChange={e => setSeriesForm({ ...seriesForm, name: e.target.value })} placeholder="Series name override (optional)"
                      data-testid="series-name-input" className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm" style={{ color: '#f5f0e8' }} />
                    <textarea value={seriesForm.description} onChange={e => setSeriesForm({ ...seriesForm, description: e.target.value })} placeholder="Description (optional)" rows={2}
                      className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm resize-none" style={{ color: '#f5f0e8' }} />
                    <input value={seriesForm.business_url} onChange={e => setSeriesForm({ ...seriesForm, business_url: e.target.value })} placeholder="Business URL (optional)"
                      data-testid="series-business-url" className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm" style={{ color: '#f5f0e8' }} />
                    <textarea value={seriesForm.extra_context} onChange={e => setSeriesForm({ ...seriesForm, extra_context: e.target.value })} placeholder="Extra context — anything that should guide all episodes" rows={2}
                      data-testid="series-extra-context" className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm resize-none" style={{ color: '#f5f0e8' }} />
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* New episode modal — with asset reuse */}
        <AnimatePresence>
          {showEpisodeForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowEpisodeForm(false)}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.8)' }}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl p-6 space-y-4 my-auto" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-lg font-semibold" style={{ color: '#f5f0e8' }}>New episode</p>
                <input value={episodeForm.title} onChange={e => setEpisodeForm({ ...episodeForm, title: e.target.value })} placeholder="Episode title"
                  data-testid="episode-title-input" className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm" style={{ color: '#f5f0e8' }} />
                <textarea value={episodeForm.script_outline} onChange={e => setEpisodeForm({ ...episodeForm, script_outline: e.target.value })} placeholder="Script outline (optional)" rows={2}
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm resize-none" style={{ color: '#f5f0e8' }} />
                <textarea value={episodeForm.extra_prompt} onChange={e => setEpisodeForm({ ...episodeForm, extra_prompt: e.target.value })} placeholder="Visuals direction — mood, characters, anything specific" rows={2}
                  data-testid="episode-extra-prompt" className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-3 text-sm resize-none" style={{ color: '#f5f0e8' }} />

                {/* Reference image picker */}
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Character / reference image (optional)</p>
                  {episodeForm.reference_image_url ? (
                    <div className="flex items-center gap-2">
                      <img src={`${API}${episodeForm.reference_image_url}`} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      <p className="text-xs flex-1 truncate" style={{ color: 'rgba(245,240,232,0.7)' }}>Reference set</p>
                      <button onClick={() => setEpisodeForm(p => ({ ...p, reference_image_url: '' }))} className="p-1.5 rounded hover:bg-white/5"><X size={12} style={{ color: '#ff6b6b' }} /></button>
                    </div>
                  ) : (
                    <button onClick={() => setShowAssetPicker('image')} data-testid="ep-pick-image"
                      className="w-full px-3 py-2.5 rounded-xl text-xs font-medium flex items-center justify-center gap-2"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.12)', color: 'rgba(245,240,232,0.6)' }}>
                      <ImageIcon size={14} /> Pick from your assets or upload
                    </button>
                  )}
                </div>

                {/* Voiceover picker */}
                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Voiceover (optional — uses TTS if empty)</p>
                  {episodeForm.voiceover_url ? (
                    <div className="flex items-center gap-2">
                      <audio src={`${API}${episodeForm.voiceover_url}`} controls className="flex-1 h-8" />
                      <button onClick={() => setEpisodeForm(p => ({ ...p, voiceover_url: '' }))} className="p-1.5 rounded hover:bg-white/5"><X size={12} style={{ color: '#ff6b6b' }} /></button>
                    </div>
                  ) : (
                    <button onClick={() => setShowAssetPicker('voice')} data-testid="ep-pick-voice"
                      className="w-full px-3 py-2.5 rounded-xl text-xs font-medium flex items-center justify-center gap-2"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.12)', color: 'rgba(245,240,232,0.6)' }}>
                      <Microphone size={14} /> Pick a saved voiceover or upload audio
                    </button>
                  )}
                  <textarea value={episodeForm.voiceover_text} onChange={e => setEpisodeForm({ ...episodeForm, voiceover_text: e.target.value })}
                    placeholder="Or type narration text to TTS verbatim (optional)" rows={2}
                    className="mt-2 w-full bg-zinc-900 border border-white/10 rounded-xl px-3 py-2 text-xs resize-none" style={{ color: '#f5f0e8' }} />
                </div>

                <div>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Tier</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setEpisodeForm({ ...episodeForm, tier: 'slideshow', duration_seconds: 30 })} data-testid="tier-slideshow"
                      className="p-3 rounded-xl text-left"
                      style={{
                        background: episodeForm.tier === 'slideshow' ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${episodeForm.tier === 'slideshow' ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                      }}>
                      <p className="text-xs font-semibold flex items-center gap-1" style={{ color: episodeForm.tier === 'slideshow' ? '#c8f060' : '#f5f0e8' }}><Lightning size={11} /> Slideshow</p>
                      <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>3 credits · ~1–2 min</p>
                    </button>
                    <button onClick={() => setEpisodeForm({ ...episodeForm, tier: 'sora', duration_seconds: 8 })} disabled={!canUseSora} data-testid="tier-sora"
                      className="p-3 rounded-xl text-left disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{
                        background: episodeForm.tier === 'sora' ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${episodeForm.tier === 'sora' ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                      }}>
                      <p className="text-xs font-semibold flex items-center gap-1" style={{ color: episodeForm.tier === 'sora' ? '#c8f060' : '#f5f0e8' }}><Crown size={11} weight="fill" /> Sora 2 {!canUseSora && '🔒'}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>$29 only · 12 credits · max 12s</p>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs" style={{ color: 'rgba(245,240,232,0.4)' }}>Duration (seconds)</label>
                  <input type="number" value={episodeForm.duration_seconds}
                    onChange={e => setEpisodeForm({ ...episodeForm, duration_seconds: Math.max(4, Math.min(parseInt(e.target.value) || 30, episodeForm.tier === 'sora' ? 12 : 60)) })}
                    min={4} max={episodeForm.tier === 'sora' ? 12 : 60}
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl px-4 py-2 text-sm mt-1" style={{ color: '#f5f0e8' }} />
                </div>

                <ReferenceMediaUpload
                  label="Add reference media (optional)"
                  note="Upload clips or photos to use as series assets."
                  acceptedTypes={['image', 'video']}
                  maxFiles={3}
                  maxSizeMB={25}
                />

                <div className="flex gap-2">
                  <button onClick={createEpisode} disabled={loading} data-testid="create-episode-submit"
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50" style={{ background: '#c8f060', color: '#080808' }}>Create episode</button>
                  <button onClick={() => setShowEpisodeForm(false)} className="px-4 rounded-xl text-sm" style={{ background: 'rgba(255,255,255,0.04)', color: '#f5f0e8' }}>Cancel</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Asset picker modal */}
        <AnimatePresence>
          {showAssetPicker && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowAssetPicker(null)} data-testid="asset-picker-modal"
              className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.85)' }}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}
                className="w-full max-w-2xl rounded-2xl p-6 space-y-4 my-auto" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold" style={{ color: '#f5f0e8' }}>{showAssetPicker === 'image' ? 'Pick a character / image' : 'Pick a voiceover'}</p>
                  <button onClick={() => setShowAssetPicker(null)} className="p-1.5 rounded-lg hover:bg-white/5"><X size={16} style={{ color: 'rgba(245,240,232,0.5)' }} /></button>
                </div>

                <label className="block">
                  <input type="file" accept={showAssetPicker === 'image' ? 'image/*' : 'audio/*'} hidden
                    onChange={e => uploadAndUseAsRef(e.target.files?.[0], showAssetPicker)} />
                  <div className="px-4 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 cursor-pointer transition-all hover:opacity-90"
                    style={{ background: '#c8f060', color: '#080808' }}>
                    <UploadSimple size={16} weight="bold" /> Upload from device
                  </div>
                </label>

                {showAssetPicker === 'image' && (
                  <>
                    {assets.avatars.length > 0 && (
                      <div>
                        <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Your characters</p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {assets.avatars.slice(0, 12).map(a => (
                            <button key={a.character_id || a.id} onClick={() => { setEpisodeForm(p => ({ ...p, reference_image_url: a.image_url || a.url || `/api/characters/file/${a.character_id || a.id}` })); setShowAssetPicker(null); }}
                              className="aspect-square rounded-lg overflow-hidden ring-1 ring-white/5 hover:ring-[#c8f060]/40 transition-all">
                              <img src={`${API}${a.image_url || a.url || `/api/characters/file/${a.character_id || a.id}`}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {assets.uploads.length > 0 && (
                      <div>
                        <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: 'rgba(245,240,232,0.4)' }}>Your uploads</p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {assets.uploads.slice(0, 12).map(u => (
                            <button key={u.file_id} onClick={() => { setEpisodeForm(p => ({ ...p, reference_image_url: u.url })); setShowAssetPicker(null); }}
                              className="aspect-square rounded-lg overflow-hidden ring-1 ring-white/5 hover:ring-[#c8f060]/40 transition-all">
                              <img src={`${API}${u.url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {assets.avatars.length === 0 && assets.uploads.length === 0 && (
                      <p className="text-xs text-center py-6" style={{ color: 'rgba(245,240,232,0.4)' }}>No saved images yet — upload one above.</p>
                    )}
                  </>
                )}

                {showAssetPicker === 'voice' && (
                  <>
                    {assets.voiceovers.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(245,240,232,0.4)' }}>Your voiceovers</p>
                        {assets.voiceovers.slice(0, 10).map(v => (
                          <button key={v.voice_id || v.id} onClick={() => { setEpisodeForm(p => ({ ...p, voiceover_url: v.url || `/api/voiceover/file/${v.voice_id || v.id}` })); setShowAssetPicker(null); }}
                            className="w-full text-left rounded-lg p-3 transition-all hover:ring-1 hover:ring-white/10"
                            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <p className="text-xs font-medium" style={{ color: '#f5f0e8' }}>{v.text?.slice(0, 60) || `Voiceover ${(v.voice_id || v.id || '').slice(0, 8)}`}</p>
                            <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.4)' }}>{v.voice || 'nova'}</p>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-center py-6" style={{ color: 'rgba(245,240,232,0.4)' }}>No saved voiceovers — upload audio above.</p>
                    )}
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Preview episode */}
        <AnimatePresence>
          {previewEp && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setPreviewEp(null)} data-testid="episode-preview-modal"
              className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.85)' }}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <video src={`${API}${previewEp.video_url}`} controls autoPlay playsInline className="w-full" style={{ aspectRatio: '9/16' }} />
                <div className="p-4 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate" style={{ color: '#f5f0e8' }}>{previewEp.title}</p>
                  <ShareSaveButton url={previewEp.video_url}
                    filename={`${previewEp.title.slice(0, 40).replace(/[^a-z0-9]/gi, '_')}.mp4`}
                    title={previewEp.title} text="Made with ScalitOS" mimeType="video/mp4" dataTestId="ep-preview" />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Ideas modal */}
        <AnimatePresence>
          {showIdeas && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowIdeas(false)} data-testid="ideas-modal"
              className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.85)' }}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()}
                className="w-full max-w-2xl rounded-2xl p-6 space-y-4 my-auto" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold" style={{ color: '#f5f0e8' }}>Series ideas for your brand</p>
                  <button onClick={() => setShowIdeas(false)} className="p-1.5 rounded-lg hover:bg-white/5"><X size={16} style={{ color: 'rgba(245,240,232,0.5)' }} /></button>
                </div>
                {ideas.length === 0 ? (
                  <p className="text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>No ideas yet.</p>
                ) : ideas.map((idea, i) => (
                  <button key={i} onClick={() => applyIdea(idea)} data-testid={`idea-${i}`}
                    className="w-full text-left rounded-xl p-4 transition-all hover:ring-1 hover:ring-white/10"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <p className="text-base font-semibold mb-1" style={{ color: '#c8f060' }}>{idea.name}</p>
                    <p className="text-xs mb-2" style={{ color: 'rgba(245,240,232,0.6)' }}>{idea.topic}</p>
                    <p className="text-xs" style={{ color: 'rgba(245,240,232,0.45)' }}>{idea.description}</p>
                    {idea.sample_episodes?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {idea.sample_episodes.slice(0, 3).map((ep, j) => (
                          <span key={j} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(200,240,96,0.06)', color: 'rgba(200,240,96,0.7)' }}>{ep}</span>
                        ))}
                      </div>
                    )}
                  </button>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
